// @ts-ignore
import type {StrapiAuthenticationResponse, StrapiUser} from "@nuxtjs/strapi/dist/runtime/types";
import type {Ref} from "vue";
import type ContentLocal from "~/libs/interfaces/ContentLocal";
import type {BaseApiFetchOptions} from "~/libs/interfaces/BaseApiFetchOptions";
import NotFoundError from "~/libs/errors/NotFoundError";
import type ContentFilters from "~/libs/interfaces/ContentFilters";
import type ContentStrapi from "~/libs/interfaces/ContentStrapi";
import contentLocalFactory from "~/libs/factories/ContentLocalFactory";

const contentStore = (type: string = 'static', key?: string) => defineStore(`content-${type}-${key}`, {
  state: () => {
    const state: {
      isLoading: Ref<boolean>,
      lastSync: Record<string, Date>,
      contents: ContentLocal[],
      content: Ref<ContentLocal | null>
      filters: ContentFilters
    } = {
      isLoading: ref(false),
      lastSync: {},
      contents: reactive([]),
      content: ref(null),
      filters: {},
    }
    return state
  },
  actions: {
    setLimit(limit: number) {
      this.limit = limit
    },
    /**
     * Get items with applied filters and presets
     * @param offset
     * @param limit
     * @return ContentLocal[]
     */
    getFilteredContents(offset: number = 0, limit: number = 10): ContentLocal[] {
      let matchedCount = 0

      const contents = this.contents.map((content) => {
        let matchFilters = true

        if (matchFilters) {
          matchedCount = matchedCount + 1
        }

        if (matchFilters && matchedCount <= offset) {
          matchFilters = false
        } else if (matchFilters && matchedCount > (offset + limit)) {
          matchFilters = false
        }

        return {
          ...content,
          isEnabled: matchFilters
        }
      })
      return contents
    },

    /**
     * Get only filtered items (remove non visible)
     * @param offset
     * @param limit
     */
    getOnlyFilteredContents(offset: number = 0, limit: number = 10): ContentLocal[] {
      const items = this.getFilteredContents(offset, limit)
      return items.filter((item) => item.isVisible)
    },

    /**
     * Get count of getFilteredContents
     * @return number
     */
    getFilteredContentsCount(): number {
      const items = this.getOnlyFilteredContents(0, this.contents.length).filter(item => item.isVisible)
      return items?.length || 0
    },

    /**
     * Get count of items
     * @return number
     */
    getContentsCount(): number {
      return this.contents.length || 0
    },
    /**
     * Get content by id
     * @param id
     */
    getContent(id: string|number): ContentLocal | undefined {
      const content = this.contents.find((item) => {
        return String(item.id) === String(id)
      })
      return content
    },
    /**
     * Get content by slug
     * @param slug
     */
    getContentBySlug(slug: string): ContentLocal | undefined {
      const content = this.contents.find((item) => {
        return String(item.slug) === String(slug)
      })
      return content
    },
    /**
     * Fetch filter changed
     * @param filters
     */
    filterChanged(filters?: ContentFilters): boolean {
      const sameArrays = (a?: string[], b?: string[]) => {
        if ((!a && b) || (a && !b)) {
          return false
        }
        if (!a && !b) {
          return true
        }
        if (a === b) {
          return true
        }
        if (a && b) {
          if (a.length !== b.length) {
            return false
          }
          const sortedA = a.sort()
          const sortedB = b.sort()

          for (let i = 0; i < sortedA.length; i++) {
            if (sortedA[i] !== sortedB[i]) {
              return false
            }
          }
        }
        return true
      }

      // check item ids filter
      if (!sameArrays(filters?.ids, this.filters.ids)) {
        return true
      }

      // check item slugs filter
      if (!sameArrays(filters?.slugs, this.filters.slugs)) {
        return true
      }

      return false
    },

    checkFiltersChanges(filters: ContentFilters): void {
      // reset items if filter changed
      if (this.filterChanged(filters)) {
        this.filters.ids = filters.ids
        this.filters.slugs = filters.slugs
        this.contents = reactive([])
        this.content = null
      }
    },

    /**
     * Fetch items from server
     * @return {Promise<void>}
     */
    async fetchContents(
      filters: ContentFilters = {},
      options: BaseApiFetchOptions = {}
    ): Promise<void> {
      const logger = useFrontendLogger()
      const persistedStore = usePersistedStore()
      const { find } = useStrapi()

      const settings = {
        forceRefetch: false,
        checkFiltersChange: true,
        ...options
      }

      // reset items if filter changed
      if (settings.checkFiltersChange) {
        this.checkFiltersChanges(filters)
      }

      // no need to fetch items
      if (!settings.forceRefetch && this.contents.length >= this.limit && !persistedStore.needSync(this.$id)) {
        return
      }

      this.isLoading = true

      const apiFilters = {
        ...(filters.ids && {
          documentId: {
            $in: filters.ids,
          }
        }),
        ...(filters.slugs && {
          slug: {
            $in: filters.slugs,
          }
        })
      }

      const params = {
        filters: apiFilters,
      }

      try {
        const result = await find<ContentStrapi>('contents', params)
        //@ts-ignore
        const items = result.data || []
        this.processData(items, this.limit === 1)
        persistedStore.setLastSync(this.$id)
        this.isLoading = false
      } catch (e) {
        logger?.error('useContentsStore:fetchContents - exception', {
          errorDetail: e,
          strapiCallParams: params,
        })
        this.isLoading = false
        throw e
      }
    },
    /**
     * Process loaded data from Strapi
     * @param newItems
     * @param isDetail
     */
    processData(newItems: ContentStrapi[], isDetail: boolean = false):void {
      if (newItems.length) {
        // check item detail load
        if (isDetail && newItems[0]) {
          this.setItem(contentLocalFactory.createContentFromStrapi(newItems[0]))
        } else if (isDetail && (!newItems || !newItems.length)) {
          throw new NotFoundError('Content not found')
        }
        if (newItems && newItems.length) {
          // add new items to list
          this.addItems(newItems.map(contentLocalFactory.createContentFromStrapi).filter(item => !!item), true)
        }
      } else if (isDetail) {
        throw new NotFoundError('Content not found')
      }
    },
    /**
     * Add items to store - remove duplicities
     * @param newItems
     * @param rewrite
     */
    addItems(newItems?: ContentLocal[], rewrite: boolean = false): void {
      newItems?.forEach((newItem) => {
        const foundIndex = this.contents.findIndex(content => content.id === newItem.id)
        if (foundIndex >= 0 && rewrite) {
          this.contents.splice(foundIndex, 1)
        }
        if (foundIndex === -1 || rewrite) {
          this.contents.push(newItem)
        }
      })
    },

    setItem(item?: ContentLocal): void {
      if (item) {
        this.content = item
      } else {
        this.content = null
      }
    }
  }
})

export const useContentStore = (type?: string, key?: string) => {
  return contentStore(type, key)()
}
